import type { SimpleIngredient } from '../types/order';

/**
 * «Απλά υλικά» (stores.warehouse = false): τα product_ingredients + store_ingredients που φέρνει
 * το GET προϊόντων στο `product.simple_ingredients` (backend routes/products.js). Οι κανόνες
 * ζουν ΕΔΩ, μία φορά, για όλες τις οθόνες που τα δείχνουν: ταμείο τραπεζιών / take away /
 * delivery (ProductCustomizeModal και τα inline modals) και εφαρμογή σερβιτόρου.
 *
 * Η λειτουργία αποθήκης (warehouse ≠ false) ΔΕΝ περνά από εδώ: εκεί τα υλικά έρχονται ανά
 * μέγεθος (useProductIngredients) με δικά τους ids.
 */

type IngredientLike = Pick<SimpleIngredient, 'name'> & Partial<SimpleIngredient>;

const hasId = (v: unknown): boolean => typeof v !== 'boolean' && Number.isInteger(Number(v)) && Number(v) > 0;

/**
 * Ποια προσφέρονται ως «χωρίς Χ»: προεπιλεγμένα (is_default όχι false — το NULL μετρά ως
 * προεπιλογή, όπως πάντα) και αφαιρούμενα (is_removable όχι false), με ταυτότητα υλικού.
 *
 * ΓΙΑΤΙ με store_ingredient_id και όχι με τη θέση στη λίστα: η θέση (0, 1, 2…) έφτανε στον
 * server ως «id» υλικού — στο δελτίο κουζίνας έβγαινε υλικό άλλου καταστήματος ή τίποτα.
 */
export function removableSimpleIngredients<T extends IngredientLike>(list: T[] | null | undefined): T[] {
  return (list || []).filter(i => !!i && i.is_default !== false && i.is_removable !== false && hasId(i.store_ingredient_id));
}

/**
 * Ποια φαίνονται στα «Έξτρα» (+/−): μόνο όσα ΔΕΝ είναι ήδη μέσα στο είδος (is_default === false)
 * Ή έχουν τιμή έξτρα > 0 (extra_price = COALESCE(extra_price_override, store_ingredients.extra_price))
 * Ή είναι ρητά διαθέσιμα ως έξτρα (is_extra_available === true).
 *
 * ΓΙΑΤΙ: πριν, ΚΑΘΕ συνδεδεμένο υλικό έβγαινε και στα «Έξτρα» — τα προεπιλεγμένα με τιμή 0
 * γίνονταν δωρεάν «έξτρα» (π.χ. «+ ΝΤΟΜΑΤΑ» χωρίς χρέωση, διπλή μερίδα στην κουζίνα).
 */
export function extraSimpleIngredients<T extends IngredientLike>(list: T[] | null | undefined): T[] {
  return (list || []).filter(i => !!i && (
    i.is_default === false || Number(i.extra_price) > 0 || i.is_extra_available === true
  ));
}

/**
 * removedIngredientNames: ένα όνομα ανά id, ΣΤΗ ΣΕΙΡΑ των ids (ο server κρατά το όνομα της ίδιας
 * θέσης για ό,τι δεν βρει στα υλικά του καταστήματος). Id που δεν υπάρχει στη λίστα → '' ώστε
 * να μη μετακινηθούν τα επόμενα ονόματα.
 */
export function simpleIngredientNames<T extends IngredientLike>(list: T[] | null | undefined, ids: Array<number | string>): string[] {
  const all = list || [];
  return (ids || []).map(id => all.find(i => !!i && hasId(i.store_ingredient_id) && Number(i.store_ingredient_id) === Number(id))?.name ?? '');
}
